<template>
  <div class="board-calendar-view">
    <div class="board-calendar-layout">
      <div class="board-calendar-main bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div class="board-calendar-toolbar">
          <button type="button" class="board-calendar-today-btn" @click="goToday">Today</button>
          <div class="board-calendar-nav">
            <button type="button" class="board-calendar-nav-btn" title="Previous month" @click="prevMonth">
              <font-awesome-icon :icon="['fas', 'chevron-left']" />
            </button>
            <span class="board-calendar-month-label">{{ calendarMonthLabel }}</span>
            <button type="button" class="board-calendar-nav-btn" title="Next month" @click="nextMonth">
              <font-awesome-icon :icon="['fas', 'chevron-right']" />
            </button>
          </div>
        </div>

        <div v-if="loading && tasks.length === 0" class="board-calendar-loading">
          <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
          <p class="text-sm text-gray-500 mt-3">Loading calendar...</p>
        </div>

        <div v-else class="board-calendar-grid-wrap" :class="{ 'board-calendar-grid-wrap--loading': loading && tasks.length > 0 }">
          <div class="board-calendar-weekdays">
            <span v-for="(label, idx) in weekdayLabels" :key="'wd-' + idx" class="board-calendar-weekday">{{ label }}</span>
          </div>
          <div class="board-calendar-grid">
            <div
              v-for="cell in calendarCells"
              :key="cell.key"
              class="board-calendar-day"
              :class="{
                'board-calendar-day--muted': !cell.inMonth,
                'board-calendar-day--today': cell.isToday,
              }"
            >
              <div class="board-calendar-day-head">
                <span class="board-calendar-day-num" :class="{ 'board-calendar-day-num--today': cell.isToday }">{{ cell.day }}</span>
              </div>
              <div class="board-calendar-day-tasks">
                <button
                  v-for="task in cell.visibleTasks"
                  :key="task.id"
                  type="button"
                  class="board-calendar-task-chip"
                  :style="{ borderLeftColor: taskAccentColor(task) }"
                  :title="task.title"
                  @click.stop="onTaskClick(task.id)"
                >
                  <span class="board-calendar-task-chip-dot" :style="{ borderColor: taskAccentColor(task) }"></span>
                  <span class="board-calendar-task-chip-title">{{ task.title }}</span>
                </button>
                <button
                  v-if="cell.hiddenCount > 0"
                  type="button"
                  class="board-calendar-more-btn"
                  :title="'Show ' + cell.hiddenCount + ' more tasks'"
                  @click.stop="openDayPopover($event, cell)"
                >
                  +{{ cell.hiddenCount }} more
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <aside class="board-calendar-sidebar bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col">
        <h3 class="board-calendar-sidebar-title">Unscheduled tasks</h3>
        <button
          v-if="canAddTask"
          type="button"
          class="board-calendar-add-task"
          @click="$emit('add-task')"
        >
          <span class="board-calendar-add-task-icon">+</span>
          Add task
        </button>

        <div v-if="loading && unscheduledTasks.length === 0" class="board-calendar-sidebar-loading">
          <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600"></div>
        </div>
        <div v-else-if="unscheduledTasks.length === 0" class="board-calendar-sidebar-empty">
          <p class="text-sm text-gray-500">No unscheduled tasks.</p>
          <p class="text-xs text-gray-400 mt-1">Tasks without a due date appear here.</p>
        </div>
        <ul v-else class="board-calendar-unscheduled-list">
          <li v-for="task in unscheduledTasks" :key="task.id">
            <button
              type="button"
              class="board-calendar-unscheduled-card"
              @click="onTaskClick(task.id)"
            >
              <span class="board-calendar-unscheduled-dot" :style="{ borderColor: taskAccentColor(task) }"></span>
              <span class="board-calendar-unscheduled-title" :title="task.title">{{ task.title }}</span>
              <span
                v-if="task.assignees && task.assignees.length > 0"
                class="board-calendar-unscheduled-avatar"
                :title="task.assignees[0].name"
              >
                {{ assigneeInitials(task.assignees[0].name) }}
              </span>
            </button>
          </li>
        </ul>
      </aside>
    </div>

    <div v-if="dayPopover" class="board-calendar-popover-backdrop" @click="closeDayPopover"></div>
    <div
      v-if="dayPopover"
      class="board-calendar-popover"
      :style="dayPopoverStyle"
      role="dialog"
      aria-label="Tasks for selected day"
      @click.stop
    >
      <div class="board-calendar-popover-head">
        <h4 class="board-calendar-popover-title">{{ dayPopoverLabel }}</h4>
        <button type="button" class="board-calendar-popover-close" title="Close" @click="closeDayPopover">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>
      <ul class="board-calendar-popover-list">
        <li v-for="task in dayPopover.tasks" :key="task.id">
          <button
            type="button"
            class="board-calendar-task-chip board-calendar-task-chip--popover"
            :style="{ borderLeftColor: taskAccentColor(task) }"
            :title="task.title"
            @click="onPopoverTaskClick(task.id)"
          >
            <span class="board-calendar-task-chip-dot" :style="{ borderColor: taskAccentColor(task) }"></span>
            <span class="board-calendar-task-chip-title">{{ task.title }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'board-calendar-view',
  props: {
    tasks: {
      type: Array,
      default: function () {
        return [];
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canAddTask: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['task-click', 'add-task'],
  data: function () {
    return {
      calendarMonth: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      dayPopover: null,
    };
  },
  computed: {
    calendarMonthLabel: function () {
      return this.calendarMonth.toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      });
    },
    weekdayLabels: function () {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    },
    dayPopoverLabel: function () {
      if (!this.dayPopover || !this.dayPopover.dateStr) {
        return '';
      }
      var parts = this.dayPopover.dateStr.split('-');
      if (parts.length !== 3) {
        return this.dayPopover.dateStr;
      }
      var d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      if (isNaN(d.getTime())) {
        return this.dayPopover.dateStr;
      }
      return d.toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    },
    dayPopoverStyle: function () {
      if (!this.dayPopover) {
        return {};
      }
      var top = this.dayPopover.top;
      var left = this.dayPopover.left;
      var width = this.dayPopover.width;
      var maxHeight = window.innerHeight - top - 16;
      if (maxHeight < 120) {
        maxHeight = 120;
      }
      return {
        top: top + 'px',
        left: left + 'px',
        width: width + 'px',
        maxHeight: maxHeight + 'px',
      };
    },
    tasksByDateKey: function () {
      var map = {};
      var list = this.tasks || [];
      for (var i = 0; i < list.length; i++) {
        var task = list[i];
        var key = this.dueDateKey(task && task.due_date);
        if (!key) {
          continue;
        }
        if (!map[key]) {
          map[key] = [];
        }
        map[key].push(task);
      }
      return map;
    },
    unscheduledTasks: function () {
      var list = this.tasks || [];
      var out = [];
      for (var i = 0; i < list.length; i++) {
        var task = list[i];
        if (!this.dueDateKey(task && task.due_date)) {
          out.push(task);
        }
      }
      return out;
    },
    calendarCells: function () {
      var year = this.calendarMonth.getFullYear();
      var month = this.calendarMonth.getMonth();
      var firstDay = new Date(year, month, 1);
      var startOffset = firstDay.getDay();
      var startDate = new Date(year, month, 1 - startOffset);
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      var cells = [];
      var map = this.tasksByDateKey;

      for (var i = 0; i < 42; i++) {
        var cellDate = new Date(startDate);
        cellDate.setDate(startDate.getDate() + i);
        var dateStr = this.formatDateKey(cellDate);
        var cellDayStart = new Date(cellDate);
        cellDayStart.setHours(0, 0, 0, 0);
        var sortedTasks = this.sortTasksByLatest(map[dateStr] || []);
        var visibleTasks = sortedTasks;
        var hiddenCount = 0;

        if (sortedTasks.length > 2) {
          visibleTasks = [sortedTasks[0]];
          hiddenCount = sortedTasks.length - 1;
        }

        cells.push({
          key: dateStr + '-' + i,
          day: cellDate.getDate(),
          inMonth: cellDate.getMonth() === month,
          isToday: cellDayStart.getTime() === today.getTime(),
          dateStr: dateStr,
          sortedTasks: sortedTasks,
          visibleTasks: visibleTasks,
          hiddenCount: hiddenCount,
        });
      }

      return cells;
    },
  },
  watch: {
    calendarMonth: function () {
      this.closeDayPopover();
    },
  },
  mounted: function () {
    var self = this;
    this._onDocumentKeydown = function (evt) {
      if (evt.key === 'Escape') {
        self.closeDayPopover();
      }
    };
    document.addEventListener('keydown', this._onDocumentKeydown);
  },
  beforeUnmount: function () {
    if (this._onDocumentKeydown) {
      document.removeEventListener('keydown', this._onDocumentKeydown);
      this._onDocumentKeydown = null;
    }
  },
  methods: {
    onTaskClick: function (taskId) {
      this.$emit('task-click', taskId);
    },
    onPopoverTaskClick: function (taskId) {
      this.closeDayPopover();
      this.onTaskClick(taskId);
    },
    openDayPopover: function (event, cell) {
      var el = event.currentTarget;
      if (!el || !cell) {
        return;
      }
      var rect = el.getBoundingClientRect();
      var popoverWidth = 280;
      var left = rect.left;
      if (left + popoverWidth > window.innerWidth - 12) {
        left = window.innerWidth - popoverWidth - 12;
      }
      if (left < 12) {
        left = 12;
      }
      var top = rect.bottom + 6;
      if (top + 200 > window.innerHeight - 12) {
        top = rect.top - 6;
        if (top < 12) {
          top = 12;
        }
      }

      this.dayPopover = {
        dateStr: cell.dateStr,
        tasks: cell.sortedTasks,
        top: top,
        left: left,
        width: popoverWidth,
      };
    },
    closeDayPopover: function () {
      this.dayPopover = null;
    },
    sortTasksByLatest: function (tasks) {
      var copy = tasks.slice();
      copy.sort(function (a, b) {
        var ta = a && a.created_at ? new Date(a.created_at).getTime() : 0;
        var tb = b && b.created_at ? new Date(b.created_at).getTime() : 0;
        if (isNaN(ta)) {
          ta = 0;
        }
        if (isNaN(tb)) {
          tb = 0;
        }
        return tb - ta;
      });
      return copy;
    },
    goToday: function () {
      var now = new Date();
      this.calendarMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    },
    prevMonth: function () {
      this.calendarMonth = new Date(
        this.calendarMonth.getFullYear(),
        this.calendarMonth.getMonth() - 1,
        1
      );
    },
    nextMonth: function () {
      this.calendarMonth = new Date(
        this.calendarMonth.getFullYear(),
        this.calendarMonth.getMonth() + 1,
        1
      );
    },
    dueDateKey: function (dueDate) {
      if (!dueDate) {
        return null;
      }
      var raw = String(dueDate).trim();
      if (!raw) {
        return null;
      }
      if (raw.length >= 10 && raw.indexOf('T') === -1) {
        return raw.substring(0, 10);
      }
      var d = new Date(dueDate);
      if (isNaN(d.getTime())) {
        if (raw.length >= 10) {
          return raw.substring(0, 10);
        }
        return null;
      }
      return this.formatDateKey(d);
    },
    formatDateKey: function (date) {
      var y = date.getFullYear();
      var m = String(date.getMonth() + 1);
      if (m.length < 2) {
        m = '0' + m;
      }
      var day = String(date.getDate());
      if (day.length < 2) {
        day = '0' + day;
      }
      return y + '-' + m + '-' + day;
    },
    taskAccentColor: function (task) {
      if (task && task.column && task.column.color) {
        return task.column.color;
      }
      var priority = task && task.priority ? task.priority : '';
      var colors = {
        low: '#10B981',
        medium: '#499DD4',
        high: '#F59E0B',
        critical: '#EF4444',
      };
      return colors[priority] || '#7c3aed';
    },
    assigneeInitials: function (name) {
      if (!name) {
        return '?';
      }
      return name
        .split(' ')
        .map(function (n) {
          return n[0];
        })
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
  },
};
</script>

<style scoped>
.board-calendar-view {
  width: 100%;
  position: relative;
}

.board-calendar-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (min-width: 1024px) {
  .board-calendar-layout {
    flex-direction: row;
    align-items: flex-start;
  }
}

.board-calendar-main {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.board-calendar-sidebar {
  width: 100%;
  max-height: 640px;
}

@media (min-width: 1024px) {
  .board-calendar-sidebar {
    width: 280px;
    flex-shrink: 0;
  }
}

.board-calendar-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.25rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.board-calendar-today-btn {
  padding: 0.375rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1e40af;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}

.board-calendar-today-btn:hover {
  background: #eff6ff;
  border-color: #93c5fd;
}

.board-calendar-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.board-calendar-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: #475569;
  cursor: pointer;
}

.board-calendar-nav-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.board-calendar-month-label {
  min-width: 10rem;
  text-align: center;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
}

.board-calendar-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
}

.board-calendar-grid-wrap {
  overflow-x: auto;
}

.board-calendar-grid-wrap--loading {
  opacity: 0.55;
  pointer-events: none;
}

.board-calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  min-width: 42rem;
}

.board-calendar-weekday {
  padding: 0.5rem 0.25rem;
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  min-width: 0;
}

.board-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  min-width: 42rem;
}

.board-calendar-day {
  height: 6.5rem;
  min-height: 6.5rem;
  max-height: 6.5rem;
  min-width: 0;
  border-right: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  padding: 0.25rem;
  background: #fff;
  overflow: hidden;
  box-sizing: border-box;
}

.board-calendar-day:nth-child(7n) {
  border-right: none;
}

.board-calendar-day--muted {
  background: #fafafa;
}

.board-calendar-day--muted .board-calendar-day-num {
  color: #94a3b8;
}

.board-calendar-day--today {
  background: #f5f3ff;
}

.board-calendar-day-head {
  display: flex;
  justify-content: flex-end;
  padding: 0.125rem 0.25rem;
  flex-shrink: 0;
}

.board-calendar-day-num {
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  line-height: 1.25rem;
  min-width: 1.5rem;
  text-align: center;
}

.board-calendar-day-num--today {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  background: #7c3aed;
  color: #fff;
  font-weight: 700;
}

.board-calendar-day-tasks {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0 0.125rem 0.125rem;
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
}

.board-calendar-task-chip {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  text-align: left;
  padding: 0.2rem 0.3rem;
  border: none;
  border-left: 3px solid #7c3aed;
  border-radius: 0.25rem;
  background: #ede9fe;
  cursor: pointer;
  transition: background-color 0.12s ease;
  flex-shrink: 0;
}

.board-calendar-task-chip:hover {
  background: #ddd6fe;
}

.board-calendar-task-chip--popover {
  width: 100%;
}

.board-calendar-task-chip-dot {
  flex-shrink: 0;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  border: 2px solid #7c3aed;
  background: #fff;
}

.board-calendar-task-chip-title {
  flex: 1;
  min-width: 0;
  font-size: 0.6875rem;
  font-weight: 500;
  color: #1e1b4b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.board-calendar-more-btn {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 0.125rem 0.25rem;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}

.board-calendar-more-btn:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.board-calendar-popover-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
  background: transparent;
}

.board-calendar-popover {
  position: fixed;
  z-index: 50;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.board-calendar-popover-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 0.875rem;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.board-calendar-popover-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f172a;
}

.board-calendar-popover-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  color: #64748b;
  cursor: pointer;
}

.board-calendar-popover-close:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.board-calendar-popover-list {
  list-style: none;
  margin: 0;
  padding: 0.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.board-calendar-popover-list li {
  margin-bottom: 0.375rem;
}

.board-calendar-popover-list li:last-child {
  margin-bottom: 0;
}

.board-calendar-sidebar-title {
  padding: 1rem 1rem 0.5rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
}

.board-calendar-add-task {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 1rem 0.75rem;
  padding: 0.5rem 0;
  border: none;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
}

.board-calendar-add-task:hover {
  color: #1d4ed8;
}

.board-calendar-add-task-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  font-size: 1rem;
  line-height: 1;
}

.board-calendar-sidebar-loading,
.board-calendar-sidebar-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  text-align: center;
}

.board-calendar-unscheduled-list {
  list-style: none;
  margin: 0;
  padding: 0 0.75rem 0.75rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.board-calendar-unscheduled-card {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  padding: 0.625rem 0.75rem;
  margin-bottom: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  min-width: 0;
}

.board-calendar-unscheduled-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.board-calendar-unscheduled-dot {
  flex-shrink: 0;
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 9999px;
  border: 2px solid #94a3b8;
  background: #fff;
}

.board-calendar-unscheduled-title {
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.board-calendar-unscheduled-avatar {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  background: #499dd4;
  color: #fff;
  font-size: 0.5625rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
